import React, { useEffect, useRef, useCallback } from 'react'; // memo,
import { HeatmapHelper, IHeatmap } from '../../../utils/HeatmapHelper';
import { sortColumns, sortRows, sortRowsByColumn, sortColumnsByRow } from '../../../utils/sorting';
import { cloneDeep } from 'lodash';
import styled from 'styled-components';
import './tooltip.css';

const Root = styled.div`
  position: absolute;
  & .name-pointer,
  .value-pointer,
  .row-pointer {
    cursor: pointer;
  }
`;

function Heatmap({
  data,
  size,
  sorting,
  onClick,
  colorScheme,
  onSortingChange,
  tooltipCfg,
  gridIndex,
}: {
  data: {
    col_nodes: Array<{ name: string; description: string }>;
    row_nodes: Array<{ name: string; description: string; id: string }>;
    data: number[][];
    type?: string;
  };
  size: { width: number; height: number };
  sorting: { col: string; row: string; rowSortAscending: boolean; colSortAscending: boolean };
  colorScheme: string[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  onSortingChange: Function;
  tooltipCfg: any;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onClick: Function;
  gridIndex: string;
}) {
  const ref = useRef<any>(null);
  const helper = useRef<HeatmapHelper>();

  const onSort = useCallback(
    (value: any, isColumn: any, isAscending: any) => {
      const name = (isColumn ? 'Row Order' : 'Column Order') as string;
      onSortingChange({ name, value, isAscending });
    },
    [onSortingChange],
  );

  useEffect(() => {
    helper.current = new HeatmapHelper(ref.current, onSort);
    helper.current.setSorting(sorting);
    if (size.width !== undefined && size.height !== undefined) {
      helper.current?.setSize(size.width, size.height);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (helper.current) helper.current.setSorting(sorting);
  }, [sorting]);

  useEffect(() => {
    if (!data) {
      return;
    }

    let heatmapData: IHeatmap = cloneDeep({
      axisYLabels: data.row_nodes,
      axisXLabels: data.col_nodes,
      matrix: data.data,
      values: [],
    });

    if (!heatmapData.axisYLabels?.length) return;
    if (!heatmapData.axisXLabels?.length) return;

    const defaultSort = (type: string) =>
      type === 'rank' || type === 'Measure' || type === 'alpha' || type === 'Sorted' || type === 'Alphabetical';
    heatmapData = defaultSort(sorting.col)
      ? sortColumns(heatmapData, sorting.col === 'alpha' || sorting.col === 'Alphabetical')
      : sortColumnsByRow(heatmapData, sorting.col, sorting.colSortAscending);
    heatmapData = defaultSort(sorting.row)
      ? sortRows(heatmapData, sorting.row === 'alpha' || sorting.row === 'Alphabetical', sorting.row === 'Measure')
      : sortRowsByColumn(heatmapData, sorting.row, sorting.rowSortAscending);

    for (let i = 0; i < heatmapData.axisYLabels?.length || 0; ++i) {
      for (let j = 0; j < heatmapData.axisXLabels?.length || 0; ++j) {
        heatmapData.values.push({
          XLabel: heatmapData.axisXLabels[j].name,
          YLabel: heatmapData.axisYLabels[i].name,
          YLabelID: heatmapData.axisYLabels[i].id,
          Value: heatmapData.matrix[i][j],
        });
      }
    }

    helper.current?.setData(heatmapData, colorScheme, tooltipCfg, gridIndex);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sorting]);

  useEffect(() => {
    if (size.width === undefined || size.height === undefined) {
      return;
    }

    helper.current?.setSize(size.width, size.height);
  }, [size]);

  useEffect(() => {
    helper.current?.setOnClick(onClick);
  }, [onClick]);

  return <Root ref={ref} />;
}
export default Heatmap;
